import { Controller, Control } from 'react-hook-form';
import { MenuPlacement } from 'react-select';
import Select from 'components/UnifiedSelect';

type HyperSelectProps = {
    name?: string;
    options: any[];
    value?: any;
    multiple?: boolean;
    placeholder?: string;
    labelKey?: string | ((option: any) => string);
    validation?: Record<string, any>;
    control?: Control<any>;
    disabled?: boolean;
    isInvalid?: boolean;
    onChange?: (...event: any[]) => void;
    uncontrolled?: boolean;
    menuPlacement?: MenuPlacement;
    closeOnSelect?: boolean;
    clearable?: boolean;
    className?: string;
    defaultValue?: any;
    isLoading?: boolean;
    loadingMessage?: string;
    isOptionDisabled?: (option: any) => boolean;
    maxMenuHeight?: number;
};

const getOptionValue = (option: any) => {
    if (option === null || option === undefined) {
        return '';
    }

    if (typeof option !== 'object') {
        return String(option);
    }

    return String(
        option.value ??
            option.id ??
            option.name ??
            option.label ??
            option.title ??
            ''
    );
};

const getOptionLabelFromKey =
    (labelKey: string | ((option: any) => string)) => (option: any) => {
        if (option === null || option === undefined) {
            return '';
        }

        if (typeof labelKey === 'function') {
            return labelKey(option);
        }

        if (typeof option !== 'object') {
            return String(option);
        }

        return String(
            option[labelKey] ??
                option.label ??
                option.name ??
                option.title ??
                option.value ??
                option.id ??
                ''
        );
    };

const HyperSelect = ({
    name,
    control,
    options,
    multiple = false,
    placeholder,
    validation,
    labelKey = 'name',
    disabled,
    isInvalid,
    value,
    onChange,
    uncontrolled,
    menuPlacement = 'auto',
    closeOnSelect = true,
    clearable,
    className,
    defaultValue,
    isLoading,
    loadingMessage,
    isOptionDisabled,
    maxMenuHeight
}: HyperSelectProps) => {
    const customStyles = {
        menu: (base) => ({
            ...base,
            width: '100%',
            minWidth: '100%'
        }),
        menuList: (base) => ({
            ...base,
            maxHeight: maxMenuHeight ? `${maxMenuHeight}px` : base.maxHeight
        }),
        option: (base, state) => ({
            ...base,
            fontWeight: state?.data?.isBold ? 700 : base.fontWeight
        }),
        singleValue: (base, state) => ({
            ...base,
            fontWeight: state?.data?.isBold ? 700 : base.fontWeight
        }),
        multiValueLabel: (base, state) => ({
            ...base,
            fontWeight: state?.data?.isBold ? 700 : base.fontWeight
        })
    };

    const selectComponents = {
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null
    };

    const safeOptions = Array.isArray(options) ? options : [];
    const selectOptions = isLoading ? [] : safeOptions;
    const getOptionLabel = getOptionLabelFromKey(labelKey);
    const sharedProps = {
        className,
        styles: customStyles,
        isMulti: multiple,
        options: selectOptions,
        placeholder,
        value,
        isLoading,
        loadingMessage: () => loadingMessage || 'Loading...',
        components: selectComponents,
        getOptionLabel,
        getOptionValue,
        isDisabled: disabled,
        isOptionDisabled,
        menuPlacement,
        closeMenuOnSelect: closeOnSelect,
        maxMenuHeight,
        invalid: isInvalid,
        isClearable: clearable
    };

    if (uncontrolled) {
        return (
            <Select
                {...sharedProps}
                defaultValue={defaultValue}
                onChange={onChange}
            />
        );
    }

    return (
        <Controller
            control={control}
            name={name}
            defaultValue={value}
            rules={validation}
            render={({
                field: { onChange: onControllerChange, value, onBlur }
            }) => (
                <Select
                    {...sharedProps}
                    value={value}
                    onBlur={onBlur}
                    onChange={(nextValue) => {
                        onControllerChange(nextValue);
                        onChange && onChange(nextValue);
                    }}
                />
            )}
        />
    );
};

export default HyperSelect;
